<template>
  <Transition name="route-fade" mode="out-in">
    <section
      v-if="showEntrySplash"
      key="entry-splash"
      class="entry-splash"
      @click="finishEntrySplash"
    >
      <div class="entry-time-mark">
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div class="entry-splash-copy">
        <span>TestJob 管理台</span>
        <h1>光阴易逝</h1>
        <p>把今天的接口验证沉淀成清晰结果</p>
      </div>

      <div class="entry-progress" aria-hidden="true">
        <span></span>
      </div>
    </section>

    <section v-else-if="!authChecked" key="session-check" class="auth-screen">
      <section class="auth-panel">
        <el-skeleton :rows="4" animated />
      </section>
    </section>

    <AuthPage v-else-if="!isAuthenticated" key="auth-page" />

    <el-container v-else key="app-shell" class="app-shell">
      <el-aside class="app-sidebar" width="264px">
        <div class="brand">
          <span class="brand-mark">TJ</span>
          <div class="brand-copy">
            <strong>TestJob</strong>
            <span>管理台</span>
          </div>
        </div>

        <el-menu :default-active="activeView" class="nav-menu" @select="switchView">
          <el-menu-item index="jobs">
            <el-icon><Tickets /></el-icon>
            <span>Job管理</span>
          </el-menu-item>
          <el-menu-item index="sharedJobs">
            <el-icon><Collection /></el-icon>
            <span>被分享的Job</span>
          </el-menu-item>
          <el-menu-item index="sharedRules">
            <el-icon><DocumentChecked /></el-icon>
            <span>被分享的Rule</span>
          </el-menu-item>
          <el-menu-item index="flows">
            <el-icon><SetUp /></el-icon>
            <span>流程管理</span>
          </el-menu-item>
          <el-menu-item index="rules">
            <el-icon><DocumentChecked /></el-icon>
            <span>Rule管理</span>
          </el-menu-item>
          <el-menu-item index="nodes">
            <el-icon><SetUp /></el-icon>
            <span>节点测试</span>
          </el-menu-item>
        </el-menu>
      </el-aside>

      <el-container class="app-body">
        <el-header class="app-header" height="auto">
          <div class="header-title">
            <h1>{{ viewTitle }}</h1>
            <div class="header-meta">
              <span>{{ jobs.length }} 个 Job</span>
              <span v-if="activeView === 'sharedJobs'">{{ jobTemplates.length }} 个分享 Job</span>
              <span v-else-if="activeView === 'sharedRules'">{{ ruleTemplates.length }} 个分享 Rule</span>
              <span v-else-if="activeView === 'flows'">{{ flows.length }} 条流程</span>
              <span v-else-if="selectedJob">当前 {{ selectedJobLabel }}</span>
              <span v-else>未选择 Job</span>
            </div>
          </div>

          <div class="header-actions">
            <span class="current-user">{{ currentUser.username }}</span>
            <el-button :icon="icons.Refresh" @click="refreshData">刷新</el-button>
            <el-button
              v-if="activeView === 'jobs'"
              type="primary"
              :icon="icons.Plus"
              @click="handleNewJob"
            >
              新增 TestJob
            </el-button>
            <el-button
              v-if="activeView === 'flows'"
              type="primary"
              :icon="icons.Plus"
              @click="handleNewFlow"
            >
              新增流程
            </el-button>
            <el-button :icon="icons.SwitchButton" @click="handleLogout">退出</el-button>
          </div>
        </el-header>

        <el-main class="app-main">
          <component :is="activePageComponent" />
        </el-main>
      </el-container>
    </el-container>
  </Transition>
</template>

<script>
// App.vue is the application shell. Page-specific forms and event handlers
// live under src/pages so each screen can evolve independently.
import { mapActions, mapMutations, mapState } from "vuex";
import {
  Collection,
  DocumentChecked,
  Plus,
  Refresh,
  SetUp,
  SwitchButton,
  Tickets,
} from "@element-plus/icons-vue";
import AuthPage from "./pages/AuthPage.vue";
import FlowsPage from "./pages/FlowsPage.vue";
import JobsPage from "./pages/JobsPage.vue";
import NodesPage from "./pages/NodesPage.vue";
import RulesPage from "./pages/RulesPage.vue";
import RuleTemplatesPage from "./pages/RuleTemplatesPage.vue";
import TemplatesPage from "./pages/TemplatesPage.vue";
import { jobOptionLabel } from "./utils/job-display";

export default {
  name: "App",

  components: {
    AuthPage,
    Collection,
    DocumentChecked,
    SetUp,
    Tickets,
    FlowsPage,
    JobsPage,
    NodesPage,
    RulesPage,
    RuleTemplatesPage,
    TemplatesPage,
  },

  data() {
    return {
      icons: {
        Plus,
        Refresh,
        SwitchButton,
      },
      entrySplashTimer: null,
      showEntrySplash: false,
    };
  },

  computed: {
    ...mapState([
      "activeView",
      "authChecked",
      "currentUser",
      "jobs",
      "jobTemplates",
      "ruleTemplates",
      "flows",
      "selectedJob",
    ]),
    ...mapState({
      isAuthenticated: (state) => Boolean(state.currentUser && state.authToken),
    }),

    viewTitle() {
      return {
        jobs: "Job 管理",
        sharedJobs: "被分享的 Job",
        sharedRules: "被分享的 Rule",
        flows: "流程管理",
        rules: "Rule 管理",
        nodes: "节点测试",
      }[this.activeView];
    },

    activePageComponent() {
      return {
        jobs: "JobsPage",
        sharedJobs: "TemplatesPage",
        sharedRules: "RuleTemplatesPage",
        flows: "FlowsPage",
        rules: "RulesPage",
        nodes: "NodesPage",
      }[this.activeView];
    },

    selectedJobLabel() {
      return jobOptionLabel(this.selectedJob);
    },
  },

  async mounted() {
    // Resolve the session before loading user-scoped Job, Rule, Flow, and DB metadata.
    const authenticated = await this.loadSession();
    if (authenticated) {
      this.refreshData();
      this.loadDatabases();
    } else {
      this.startEntrySplash();
    }
  },

  beforeUnmount() {
    if (this.entrySplashTimer) {
      window.clearTimeout(this.entrySplashTimer);
    }
  },

  methods: {
    ...mapActions([
      "loadSession",
      "logout",
      "loadJobs",
      "loadJobTemplates",
      "loadRuleTemplates",
      "loadAllRules",
      "loadFlows",
      "loadDatabases",
      "switchView",
    ]),
    ...mapMutations({
      resetJobFormState: "resetJobForm",
      resetFlowFormState: "resetFlowForm",
    }),

    handleNewJob() {
      this.switchView("jobs");
      this.resetJobFormState();
    },

    handleNewFlow() {
      this.switchView("flows");
      this.resetFlowFormState();
    },

    async handleLogout() {
      await this.logout();
      this.startEntrySplash();
    },

    startEntrySplash() {
      if (this.entrySplashTimer) {
        window.clearTimeout(this.entrySplashTimer);
      }
      this.showEntrySplash = true;
      this.entrySplashTimer = window.setTimeout(this.finishEntrySplash, 1800);
    },

    finishEntrySplash() {
      if (this.entrySplashTimer) {
        window.clearTimeout(this.entrySplashTimer);
        this.entrySplashTimer = null;
      }
      this.showEntrySplash = false;
    },

    refreshData() {
      // These lists are shared by the sidebar counts and the main management pages.
      if (!this.isAuthenticated) {
        return;
      }
      this.loadJobs();
      this.loadJobTemplates();
      this.loadRuleTemplates();
      this.loadAllRules();
      this.loadFlows();
    },
  },
};
</script>
